// Zahlungsplan utility functions
// Extracted from Controlling.jsx for reuse in ZahlungsplanModal

// ISO Kalenderwoche berechnen
export const getISOWeekNumber = (date) => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

// Get Monday of a given ISO week
export const getWeekStartDate = (weekNum, year) => {
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dayOfWeek + 1);
  monday.setDate(monday.getDate() + (weekNum - 1) * 7);
  return monday;
};

// Get Sunday of a given ISO week
export const getWeekEndDate = (weekNum, year) => {
  const weekStart = getWeekStartDate(weekNum, year);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  return weekEnd;
};

// Parse date string to local date (avoids timezone issues)
// Tippfehler wie "0025-06-28" werden als ungültig zurückgewiesen (sonst mappt JS auf 1925)
export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split("T")[0].split("-");
  if (parts.length !== 3) {
    const fallback = new Date(dateStr);
    if (isNaN(fallback.getTime()) || fallback.getFullYear() < 1900) return null;
    return fallback;
  }
  const year = parseInt(parts[0]);
  if (!Number.isFinite(year) || year < 1900) return null;
  return new Date(year, parseInt(parts[1]) - 1, parseInt(parts[2]));
};

// Get effective week for a date (weekend dates assigned to previous week)
export const getEffectiveWeekForDate = (dateStr) => {
  const date = parseLocalDate(dateStr);
  if (!date || isNaN(date.getTime())) return null;

  const dayOfWeek = date.getDay();
  if (dayOfWeek === 0) {
    date.setDate(date.getDate() - 2);
  } else if (dayOfWeek === 6) {
    date.setDate(date.getDate() - 1);
  }

  const weekNum = getISOWeekNumber(date);
  const year = date.getFullYear();
  const month = date.getMonth();
  let weekYear = year;
  if (month === 0 && weekNum > 50) {
    weekYear = year - 1;
  } else if (month === 11 && weekNum === 1) {
    weekYear = year + 1;
  }

  return { weekNum, year: weekYear, adjustedDate: date };
};

// Calculate invoice date for a given week + day of week
// dayOfWeek: 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday
export const getInvoiceDateForWeek = (weekNum, year, dayOfWeek = 5) => {
  const monday = getWeekStartDate(weekNum, year);
  const invoiceDate = new Date(monday);
  invoiceDate.setDate(monday.getDate() + (dayOfWeek - 1));
  return invoiceDate;
};

// Format date as YYYY-MM-DD
export const formatDateISO = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Format date as DD.MM.YYYY (Swiss)
export const formatDateSwiss = (date) => {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}.${m}.${y}`;
};

// Swiss currency formatting (netto, no CHF prefix)
export const formatCurrencyNetto = (amount) => {
  if (amount === 0 || amount === null || amount === undefined) return "";
  return new Intl.NumberFormat("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Swiss currency formatting with CHF prefix
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Parse Swiss number format (e.g., "20'000.00" or "20000")
export const parseSwissNumber = (str) => {
  if (!str) return NaN;
  return parseFloat(String(str).replace(/'/g, "").replace(/\s/g, ""));
};

// Generate week range from phase dates
// Returns array of {number, year, startDate, isCurrentWeek}
export const generateWeeksForPhases = (phases, extraWeeks = 8, entries = []) => {
  if (!phases || phases.length === 0) return [];

  const today = new Date();
  const currentWeekNum = getISOWeekNumber(today);
  const currentYear = today.getFullYear();

  // Find earliest start and latest end across all phases
  let earliestStart = null;
  let latestEnd = null;

  phases.forEach((phase) => {
    if (phase.startDate) {
      const start = parseLocalDate(phase.startDate);
      if (start && (!earliestStart || start < earliestStart)) {
        earliestStart = start;
      }
    }
    if (phase.endDate) {
      const end = parseLocalDate(phase.endDate);
      if (end && (!latestEnd || end > latestEnd)) {
        latestEnd = end;
      }
    }
  });

  // Also consider entry dates so entries outside the phase range have a week row
  entries.forEach((entry) => {
    const dateStr = entry.wann || entry.versand_datum || entry.paid_date || entry.created_at;
    if (!dateStr) return;
    const d = parseLocalDate(dateStr);
    if (!d || isNaN(d.getTime())) return;
    if (!earliestStart || d < earliestStart) earliestStart = d;
    if (!latestEnd || d > latestEnd) latestEnd = d;
  });

  // Fallback: if no dates, use current year
  if (!earliestStart) {
    earliestStart = new Date(currentYear, 0, 1);
  }
  if (!latestEnd) {
    latestEnd = new Date(currentYear, 11, 31);
  }

  // Add extra weeks buffer at the end
  const bufferedEnd = new Date(latestEnd);
  bufferedEnd.setDate(bufferedEnd.getDate() + extraWeeks * 7);

  // Generate weeks from earliest to buffered end
  const weeks = [];
  let currentDate = new Date(earliestStart);
  // Go to Monday of the starting week
  const startDayOfWeek = currentDate.getDay() || 7;
  currentDate.setDate(currentDate.getDate() - (startDayOfWeek - 1));

  // Sicherheitsnetz: bei verkacktem Datumsbereich (Tippfehler im Jahr) max. 10 Jahre rendern
  const MAX_WEEKS = 520;

  while (currentDate <= bufferedEnd && weeks.length < MAX_WEEKS) {
    const weekNum = getISOWeekNumber(currentDate);
    const year = currentDate.getFullYear();
    // Handle year boundary for week year
    const month = currentDate.getMonth();
    let weekYear = year;
    if (month === 0 && weekNum > 50) {
      weekYear = year - 1;
    } else if (month === 11 && weekNum === 1) {
      weekYear = year + 1;
    }

    weeks.push({
      number: weekNum,
      year: weekYear,
      startDate: new Date(currentDate),
      isCurrentWeek: weekNum === currentWeekNum && weekYear === currentYear,
    });

    currentDate.setDate(currentDate.getDate() + 7);
  }

  return weeks;
};

// Generate invoice reference number
// Format: {prefix}-{phaseNumber}-{laufnummer}
export const generateInvoiceReference = (prefix, phaseName, existingEntries) => {
  // Extract phase number from phase name (e.g., "21" from "21 - Machbarkeitsstudie")
  const phaseMatch = phaseName?.match(/^(\d+)/);
  const phaseNumber = phaseMatch ? phaseMatch[1] : "00";

  // Count existing Zahlungsplan entries for this phase
  const existingCount = existingEntries.filter(
    (e) => e.source === "zahlungsplan"
  ).length;

  const laufnummer = String(existingCount + 1).padStart(3, "0");
  return `${prefix}-${phaseNumber}-${laufnummer}`;
};

// Build a lookup key for the entry map
export const buildCellKey = (typ, phaseKey, weekKey, partnerId) => {
  if (typ === "Einnahme") {
    return `einnahmen|${phaseKey}|${weekKey}`;
  }
  return `ausgaben|${phaseKey}|${partnerId || "other"}|${weekKey}`;
};

// Build the entry map from budget entries for fast cell access.
// Pro Zelle ein ARRAY: mehrere Einträge in derselben (Typ|Phase|Woche|Partner)-
// Zelle wurden früher stumm überschrieben — das Raster zeigte weniger als die
// Fusszeile summierte. Jetzt zeigen Zellen die Summe + Marker (Fix 2026-07-24).
export const buildEntryMap = (entries) => {
  const map = {};
  entries.forEach((entry) => {
    if (entry.typ_timing === "Regelmässig") return;

    // Fallback date chain: wann → versand_datum → paid_date → created_at
    const dateStr = entry.wann || entry.versand_datum || entry.paid_date || entry.created_at;
    const effectiveWeek = getEffectiveWeekForDate(dateStr);
    if (!effectiveWeek) return;

    const weekKey = `${effectiveWeek.year}-${effectiveWeek.weekNum}`;
    const phaseKey = entry.phase || "unassigned";

    const key = buildCellKey(entry.typ, phaseKey, weekKey, entry.partner_id);
    if (!map[key]) map[key] = [];
    map[key].push(entry);
  });

  return map;
};

// Summe der Netto-Beträge einer Zellen-Eintragsliste
export const sumCellEntries = (cellEntries = []) =>
  cellEntries.reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);

// Phase kann als Name ODER als Phasen-ID gespeichert sein (alte Budget-Einträge);
// für die Anzeige immer den Namen auflösen.
export const resolvePhaseName = (phaseValue, phases = []) => {
  if (!phaseValue) return "";
  const match = phases.find((p) => p.id === phaseValue || p.name === phaseValue);
  return match?.name || phaseValue;
};

// Status display configuration
export const STATUS_CONFIG = {
  expected: { label: "Geplant", color: "rgba(148, 163, 184, 0.18)", textColor: "inherit" },
  expecting: { label: "Erwartet", color: "rgba(148, 163, 184, 0.18)", textColor: "inherit" },
  sent: { label: "Versendet", color: "rgba(250, 204, 21, 0.2)", textColor: "#fde047" },
  received: { label: "Erhalten", color: "rgba(250, 204, 21, 0.2)", textColor: "#fde047" },
  entered_payment: { label: "Zahlung ausgelöst", color: "rgba(250, 204, 21, 0.2)", textColor: "#fde047" },
  paid: { label: "Bezahlt", color: "rgba(74, 222, 128, 0.2)", textColor: "#4ade80" },
  overdue: { label: "Überfällig", color: "rgba(248, 113, 113, 0.2)", textColor: "#f87171" },
};

// Day of week labels
export const DAY_OPTIONS = [
  { value: 1, label: "Montag" },
  { value: 2, label: "Dienstag" },
  { value: 3, label: "Mittwoch" },
  { value: 4, label: "Donnerstag" },
  { value: 5, label: "Freitag" },
];
